import React, { useEffect, useState, useCallback } from 'react';
import { useMiddleman } from '@hubins/middleman';
import Button from '@hubins/components/Button';
import { /*rolesTableHeaders,*/ rolesLoadingState } from 'helpers/roles';
import { Columns, Column } from 'components/Columns';
import SimpleTable from 'modules/Tables/SimpleTable';
import Heading from '@hubins/components/Heading';
import AddUserToProfileModal from './AddUserToProfileModal';
import EditProfileUserInfoModal from './EditProfileUserInfoModal';
import __ from 'localisation';
const rolesTableHeaders = [
  {
    Header: __('name'),
    accessor: 'name',
  },
  {
    Header: __('ssn_org_num'),
    accessor: 'national_identity_number',
  },
  {
    Header: "",
    accessor: 'action',
    width: 40
  }
];

const personnelTableHeaders = [
  {
    Header: __('name'),
    accessor: 'name',
  },
  {
    Header: __('ssn_org_num'),
    accessor: 'national_identity_number',
  },
  {
    Header: __('ownership_percentage'),
    accessor: 'info.ownership_percentage',
  },
  {
    Header: __('role'),
    accessor: 'info.position',
  },
  {
    Header: "",
    accessor: 'edit',
    width: 40
  },
  {
    Header: "",
    accessor: 'action',
    width: 40
  },
];


const ProfileRoles = ({ profileUuid }) => {
  const middleman = useMiddleman();

  const [signatories, setSignatories] = useState(rolesLoadingState);
  const [beneficialOwners, setBeneficialOwners] = useState(rolesLoadingState);
  const [
    investmentRepresentatives,
    setInvestmentRepresentatives
  ] = useState(rolesLoadingState);
  const [alternativeBeneficialOwners, setAlternativeBeneficialOwners] = useState(rolesLoadingState);
  const [roleToAdd, setRoleToAdd] = useState('');
  const [loading, setLoading] = useState(false);
  const [profileToEdit, setProfileToEdit] = useState(false);


  const fetchProfileWithRoles = useCallback(async () => {

    setLoading(true);

    const deleteRoleForProfile = async (role, user) => {
      if (confirm(`Are you sure you want to remove ${user.name} as ${__(role)}?`)) {
        await middleman.removeUserProfileRole(profileUuid, user.uuid, role);
        window.location.reload();
      }
    };

    const deleteRoleButton = (role, profile) => {
      return <Button
        loading={loading}
        disabled={loading}
        onlyIcon
        className=''
        icon="Bin by Streamlinehq"
        onClick={() => deleteRoleForProfile(role, profile)} />;
    };

    const editProfile = (profile) => {
      setProfileToEdit(profile);
    };

    const editButton = (profile) => {
      return <Button
        loading={loading}
        disabled={loading}
        onlyIcon
        className=''
        icon="pencil write 2"
        onClick={() => editProfile(profile)} />;
    };

    try {
      const response = await middleman.get(`/profile/${profileUuid}?with=personnel`);
      const profile = response.data;
      const rawSignatories = profile.signatories || [];
      const rawBeneficialOwners = profile.beneficial_owners || [];
      const rawInvestmentRepresentatives = profile.investment_representatives || [];
      const rawAlternativeBeneficialOwners = profile.alternative_beneficial_owners || [];

      const signatoriesWithTableData = [
        ...rawSignatories.map((prof) => ({
          ...prof,
          action: deleteRoleButton('signatory', prof),
          edit: editButton(prof)
        }))
      ];

      const beneficialOwnersWithTableData = [
        ...rawBeneficialOwners.map((prof) => ({
          ...prof,
          action: deleteRoleButton('beneficial_owner', prof),
          edit: editButton(prof)
        }))
      ];

      const investmentRepresentativesWithTableData = [
        ...rawInvestmentRepresentatives.map((prof) => ({
          ...prof,
          action: deleteRoleButton('investment_representative', prof)
        }))
      ];

      const alternativeBeneficialOwnersWithTableData = [
        ...rawAlternativeBeneficialOwners.map((prof) => ({
          ...prof,
          action: deleteRoleButton('alternative_beneficial_owner', prof),
          edit: editButton(prof)
        }))
      ];

      setSignatories(signatoriesWithTableData);
      setBeneficialOwners(beneficialOwnersWithTableData);
      setInvestmentRepresentatives(investmentRepresentativesWithTableData);
      setAlternativeBeneficialOwners(alternativeBeneficialOwnersWithTableData);
    } catch (e) {
      console.error(e.message);
    }
    setLoading(false);
  }, [
    profileUuid,
    setSignatories,
    setBeneficialOwners,
    setInvestmentRepresentatives,
    setLoading,
    setRoleToAdd,
    loading]);

  useEffect(() => {
    fetchProfileWithRoles();
  }, []);

  return (
    <Columns>
      <AddUserToProfileModal
        roleToAdd={roleToAdd}
        setRoleToAdd={setRoleToAdd}
        profileUuid={profileUuid}
      />
      {profileToEdit && (
        <EditProfileUserInfoModal
          profile={profileToEdit}
          setProfile={setProfileToEdit}
          refetch={fetchProfileWithRoles}
        />
      )}
      <Column m="6">
        <Heading size="4" className="s-bottom-md">
          Signatories
        </Heading>
        <SimpleTable
          // data={signatories}
          data={[
            {
              name: "Add new signatory",
              national_identity_number: "",
              action: <Button
                onClick={() => { setRoleToAdd('signatory'); }}
                className='transparent'
                icon="add"
                onlyIcon
              />
            }, ...signatories
          ]}
          headers={personnelTableHeaders}
        />
      </Column>
      <Column m="6">
        <Heading size="4" className="s-bottom-md">
          Beneficial owners
        </Heading>
        <SimpleTable
          headers={personnelTableHeaders}
          data={[
            {
              name: "Add new beneficial owner",
              national_identity_number: "",
              action: <Button
                onClick={() => { setRoleToAdd('beneficial_owner'); }}
                className='transparent'
                icon="add"
                onlyIcon
              />
            },
            ...beneficialOwners]}
        />
      </Column>
      <Column m="6">
        <Heading size="4" className="s-bottom-md">
          Alternative Beneficial owners
        </Heading>
        <SimpleTable
          headers={personnelTableHeaders}
          data={[
            {
              name: "Add new alternative beneficial owner",
              national_identity_number: "",
              action: <Button
                onClick={() => { setRoleToAdd('alternative_beneficial_owner'); }}
                className='transparent'
                icon="add"
                onlyIcon
              />
            },
            ...alternativeBeneficialOwners]}
        />
      </Column>
      <Column m="6">
        <Heading size="4" className="s-bottom-md">
          Company representatives
        </Heading>
        <SimpleTable
          headers={rolesTableHeaders}
          data={[
            {
              name: "Add new investment representative",
              national_identity_number: "",
              action: <Button
                onClick={() => { setRoleToAdd('investment_representative'); }}
                className='transparent'
                icon="add"
                onlyIcon
              />
            },
            ...investmentRepresentatives
          ]}
        />
      </Column>
    </Columns>
  );

};

export default ProfileRoles;
